/**
 * Staffelkorting voor CUSTOM-producten.
 *
 * `specifications.volumeDiscounts` geldt voor het hele product. Bij producten
 * waar de leverancierskorting per uitvoering verschilt (neon-print: Sign Again
 * is lineair, Forex en plexiglas niet, en kleine stukken krijgen bij Probo veel
 * meer korting dan grote) is een productbrede tabel alleen veilig als hij de
 * kleinste korting van alle uitvoeringen draagt.
 *
 * `specifications.optionVolumeDiscounts` is een optionele lijst regels die de
 * productbrede tabel vervangt zodra een regel past:
 *
 *   { conditions: { material: 'black_forex_3mm' }, maxAreaM2: 0.16,
 *     tiers: [{ minQuantity: 5, discount: 0.65 }] }
 *
 * - `conditions`: optie-id -> waarde of lijst waarden; alle moeten passen.
 * - `minAreaM2` / `maxAreaM2` (optioneel): oppervlak van één stuk, grenzen
 *   inclusief. Een regel met een oppervlaktegrens past niet als het oppervlak
 *   onbekend is.
 * - De eerste passende regel wint, ook als zijn tabel leeg is (bewust geen
 *   korting voor die uitvoering).
 *
 * De korting blijft een stapfunctie op het aantal: een aantal tussen twee
 * staffels krijgt de korting van de lagere staffel, dus de hogere stukprijs
 * (de gedeelde aantalregel uit open-custom-quantity.ts).
 *
 * Gedeeld door de server (calculateCustomProductPrice) en de configurator,
 * zodat beide dezelfde korting rekenen.
 */

export type VolumeDiscountTier = {
  minQuantity?: unknown;
  quantity?: unknown;
  discount?: unknown;
  discountRate?: unknown;
};

export type OptionVolumeDiscountRule = {
  conditions?: Record<string, unknown> | null;
  minAreaM2?: unknown;
  maxAreaM2?: unknown;
  tiers?: VolumeDiscountTier[] | null;
};

type SpecificationsLike = {
  volumeDiscounts?: unknown;
  optionVolumeDiscounts?: unknown;
  [key: string]: unknown;
} | null | undefined;

export function getVolumeDiscountRate(tiers: unknown, quantity: number): number {
  if (!Array.isArray(tiers) || !Number.isFinite(quantity) || quantity <= 0) return 0;
  return tiers.reduce((best: number, tier: VolumeDiscountTier) => {
    const minQuantity = Number(tier?.minQuantity || tier?.quantity || 0);
    const discount = Number(tier?.discount || tier?.discountRate || 0);
    if (quantity >= minQuantity && Number.isFinite(discount) && discount > best) return discount;
    return best;
  }, 0);
}

function conditionsMatch(conditions: Record<string, unknown> | null | undefined, configuration: Record<string, unknown>) {
  if (!conditions || typeof conditions !== 'object') return true;
  return Object.entries(conditions).every(([field, expected]) => {
    const selected = configuration[field];
    return Array.isArray(expected)
      ? expected.map(String).includes(String(selected))
      : String(selected) === String(expected);
  });
}

function areaMatches(rule: OptionVolumeDiscountRule, pieceAreaM2: number | null | undefined) {
  const hasMin = rule.minAreaM2 !== undefined && rule.minAreaM2 !== null;
  const hasMax = rule.maxAreaM2 !== undefined && rule.maxAreaM2 !== null;
  if (!hasMin && !hasMax) return true;
  const area = Number(pieceAreaM2);
  if (!Number.isFinite(area) || area <= 0) return false;
  // Kleine marge voor afronding: 40 x 40 cm is 0,16 m2, ook als het als
  // 0,16000000000000003 binnenkomt.
  const epsilon = 1e-9;
  if (hasMin && area < Number(rule.minAreaM2) - epsilon) return false;
  if (hasMax && area > Number(rule.maxAreaM2) + epsilon) return false;
  return true;
}

/**
 * De staffeltabel die voor deze configuratie geldt: de eerste passende regel
 * uit `optionVolumeDiscounts`, anders `volumeDiscounts`.
 */
export function resolveVolumeDiscountTiers(
  specifications: SpecificationsLike,
  configuration: Record<string, unknown>,
  pieceAreaM2?: number | null,
): VolumeDiscountTier[] {
  const rules = Array.isArray(specifications?.optionVolumeDiscounts)
    ? (specifications!.optionVolumeDiscounts as OptionVolumeDiscountRule[])
    : [];
  for (const rule of rules) {
    if (!rule || typeof rule !== 'object') continue;
    if (!conditionsMatch(rule.conditions, configuration || {})) continue;
    if (!areaMatches(rule, pieceAreaM2)) continue;
    return Array.isArray(rule.tiers) ? rule.tiers : [];
  }
  if (Array.isArray(specifications?.volumeDiscounts)) return specifications!.volumeDiscounts as VolumeDiscountTier[];
  return standardVolumeDiscountTiers(specifications);
}

/**
 * Standaardstaffel voor producten zonder eigen of leveranciersstaffels
 * (besluit Gert 14-10-2026): alleen voor zover er ruimte is boven de
 * minimale opslag van 2,2x. Een product op precies 2,2x krijgt dus geen
 * korting; een product op 2,5x maximaal 12%.
 *
 * Geen standaardstaffel als het product al staffels heeft: een expliciete
 * `volumeDiscounts` (ook een lege lijst = bewust geen korting), een matrix met
 * `copies` in de sleutel (leveranciersstaffels) of oppervlakteprijzen.
 */
export const STANDARD_VOLUME_DISCOUNTS: Array<{ minQuantity: number; discount: number }> = [
  { minQuantity: 5, discount: 0.03 },
  { minQuantity: 10, discount: 0.05 },
  { minQuantity: 25, discount: 0.08 },
  { minQuantity: 50, discount: 0.1 },
  { minQuantity: 100, discount: 0.12 },
  { minQuantity: 250, discount: 0.15 },
  { minQuantity: 500, discount: 0.2 },
];
export const MINIMUM_PRICE_MULTIPLIER = 2.2;

export function standardVolumeDiscountTiers(specifications: SpecificationsLike): VolumeDiscountTier[] {
  const specs = (specifications || {}) as Record<string, any>;
  const keyFields = Array.isArray(specs.priceMatrix?.keyFields) ? specs.priceMatrix.keyFields.map(String) : [];
  if (specs.priceMatrix?.enabled !== false && keyFields.includes('copies')) return [];
  if (specs.areaPricing || specs.pieceFormulaPricing) return [];
  const multiplier = Number(specs.priceMultiplier);
  if (!Number.isFinite(multiplier) || multiplier <= MINIMUM_PRICE_MULTIPLIER) return [];
  const maxDiscount = 1 - MINIMUM_PRICE_MULTIPLIER / multiplier;
  return STANDARD_VOLUME_DISCOUNTS.filter((tier) => tier.discount <= maxDiscount + 1e-9);
}

export function resolveVolumeDiscountRate(
  specifications: SpecificationsLike,
  configuration: Record<string, unknown>,
  quantity: number,
  pieceAreaM2?: number | null,
): number {
  return getVolumeDiscountRate(resolveVolumeDiscountTiers(specifications, configuration, pieceAreaM2), quantity);
}

/** Alle staffelgrenzen die voor deze configuratie een prijsrij verdienen. */
export function volumeDiscountQuantities(
  specifications: SpecificationsLike,
  configuration: Record<string, unknown>,
  pieceAreaM2?: number | null,
): number[] {
  return resolveVolumeDiscountTiers(specifications, configuration, pieceAreaM2)
    .map((tier) => Number(tier?.minQuantity || tier?.quantity || 0))
    .filter((value) => Number.isFinite(value) && value > 0);
}
